import React, { useEffect, useState } from "react";
import "./checkbox.scss";

import { TreeNode, TreeNodeComponentProps } from "./interface";

// function to make the array of selected child permission array
const extractAllPermCodes = (node: TreeNode): string[] => {
    const result: string[] = [];

    // Recursive helper function to walk through each permission item
    const traverse = (items: TreeNode[]) => {
        // Safety check to prevent runtime errors
        if (!Array.isArray(items)) return;

        for (const item of items) {
            // Collect the current item's perm_code
            result.push(item.perm_code);
            // Recursively process children, if they exist
            if (Array.isArray(item.child) && item.child.length > 0) {
                traverse(item.child);
            }
        }
    };

    // Kick off the recursion with provided permissions
    traverse(node.child || []);
    return result;
};
// end

// function to check all inner child is checked or unchecked
const isAllInnerChecked = (
    previousNode: TreeNode,
    selectedCodes: string[]
): boolean => {
    let result: boolean = true;

    // Recursive helper function to walk through each permission item
    const traverse = (items: TreeNode[]) => {
        // Safety check to prevent runtime errors
        if (!Array.isArray(items)) return;

        for (const item of items) {
            // Collect the current item's perm_code
            if (!selectedCodes?.includes(item?.perm_code)) {
                result = false;
            }
            // Recursively process children, if they exist
            if (Array.isArray(item.child) && item.child.length > 0) {
                traverse(item.child);
            }
        }
    };

    // Kick off the recursion with provided permissions
    traverse(previousNode.child || []);
    return result;
};
// end

// function to check if any child is checked but not all
const hasPartialChildSelection = (
    node: TreeNode,
    selectedCodes: string[]
): boolean => {
    if (!node.child || node.child.length === 0) return false;

    let checkedCount = 0;
    let totalCount = 0;

    const countCheckedChildren = (items: TreeNode[]) => {
        for (const item of items) {
            totalCount++;
            if (selectedCodes.includes(item.perm_code)) {
                checkedCount++;
            }
            if (item.child && item.child.length > 0) {
                countCheckedChildren(item.child);
            }
        }
    };

    countCheckedChildren(node.child);
    return checkedCount > 0 && checkedCount < totalCount;
};
// end

const TreeNodeComponent: React.FC<TreeNodeComponentProps> = ({
    node,
    selectedCodes,
    setSelectedCodes,
    setHasParentEmpty,
    previousNode,
}) => {
    const [isChecked, setIsChecked] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [hasChildEmpty, setHasChildEmpty] = useState<boolean>(false);
    const [shouldTriggerChange, setShouldTriggerChange] = useState(true);

    const selectedCode = selectedCodes ? selectedCodes : [];
    useEffect(() => {
        if (selectedCode && selectedCode.includes(node.perm_code)) {
            setIsChecked(true);
            setIsOpen(true);
        } else {
            setIsChecked(false);
            setIsOpen(false);
        }

        // Check if this node has partial child selection (some children checked but not all)
        if (node?.child && node?.child.length > 0) {
            const hasPartialSelection = hasPartialChildSelection(node, selectedCode);
            const isNodeChecked = selectedCode.includes(node.perm_code);
            const allChildrenChecked = isAllInnerChecked(node, selectedCode);

            // Show circle checkbox if:
            // 1. Node is checked but not all children are checked, OR
            // 2. Node is not checked but some children are checked
            const shouldShowCircle =
                (isNodeChecked && !allChildrenChecked) ||
                (!isNodeChecked && hasPartialSelection);
            setHasChildEmpty(shouldShowCircle);
        } else {
            setHasChildEmpty(false);
        }
    }, [selectedCode, node.perm_code]);

    const handleCheckboxChange = (code: string, children: TreeNode[]) => {
        let updatedSelectedCodes;
        if (isChecked) {
            // Function to get all descendant codes
            const getAllDescendantCodes = (nodes: TreeNode[]) => {
                let codes: string[] = [];
                nodes.forEach((node) => {
                    codes.push(
                        node.perm_code,
                        ...getAllDescendantCodes(node.child || [])
                    );
                });
                return codes;
            };

            // Get all descendant codes and remove them from selectedCodes
            const descendantCodes = getAllDescendantCodes(children);
            updatedSelectedCodes = selectedCode.filter(
                (item: string) => item !== code && !descendantCodes.includes(item)
            );
            setHasParentEmpty && setHasParentEmpty(true);
            if (hasChildEmpty) setHasChildEmpty(false);
        } else {
            let allPermissions: string[] = extractAllPermCodes(node);
            updatedSelectedCodes = [...selectedCode, code, ...allPermissions];
            if (isAllInnerChecked(previousNode, updatedSelectedCodes)) {
                setHasParentEmpty && setHasParentEmpty(false);
            }
        }

        setIsChecked(!isChecked);
        setIsOpen(!isOpen);
        setSelectedCodes(updatedSelectedCodes);
    };

    useEffect(() => {
        if (hasChildEmpty) {
            setHasParentEmpty && setHasParentEmpty(true);
        }
        if (!hasChildEmpty) {
            if (isAllInnerChecked(previousNode, selectedCodes)) {
                setHasParentEmpty && setHasParentEmpty(false);
            }
        }
    }, [hasChildEmpty]);

    const onCheckboxTrigger = () => {
        if (!shouldTriggerChange) return;
        setShouldTriggerChange(false);
        handleCheckboxChange(node.perm_code, node.child);
        setTimeout(() => setShouldTriggerChange(true), 100);
    };

    return (
        <li className="list-unstyled permission-checkbox ">
            <label className="permission-label fs-10 fs-md-14">
                <input
                    type="checkbox"
                    className={` me-2 my-2 ${hasChildEmpty ? "permission-custom-checkbox" : ""
                        }`}
                    checked={isChecked}
                    onChange={onCheckboxTrigger}
                />
                {node.module_name}
            </label>
            {isOpen && node.child.length > 0 && (
                <ul>
                    {node.child.map((child: any, index: number) => (
                        <TreeNodeComponent
                            key={index}
                            node={child}
                            selectedCodes={selectedCodes}
                            setSelectedCodes={setSelectedCodes}
                            setHasParentEmpty={setHasChildEmpty}
                            previousNode={node}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
};

const UserPermissionUI = ({
    structuredPermission,
    handleCheckboxChange,
    defaultPermission,
}: any) => {
    const [selectedCodes, setSelectedCodes] = useState<string[]>([]);

    const extractPermCodes = (permission: any) => [
        permission.perm_code,
        ...(permission.child
            ? permission && permission.child.flatMap(extractPermCodes)
            : []),
    ];

    useEffect(() => {
        if (defaultPermission) {
            const extractedCodes =
                defaultPermission && defaultPermission.flatMap(extractPermCodes);
            setSelectedCodes(extractedCodes);
        }
    }, [defaultPermission]);
    useEffect(() => {
        handleCheckboxChange(selectedCodes);
    }, [selectedCodes]);

    return (
        <div id="tree">
            <ul>
                {structuredPermission &&
                    structuredPermission.map((node: any, index: number) => (
                        <TreeNodeComponent
                            key={index}
                            node={node}
                            selectedCodes={selectedCodes}
                            setSelectedCodes={setSelectedCodes}
                            previousNode={node}
                        />
                    ))}
            </ul>
        </div>
    );
};

export default UserPermissionUI;
